import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { firstValueFrom } from "rxjs";
import { StorageService } from "./storage.service";
import { IUserInvite, IUserInviteStatus, StorageKey } from "../models";
import { UserService } from "./user.service";
import { ApiService } from "./api";
import { environment } from "src/environments/environment";
import { CatchErrorService } from "./catch.service";

@Injectable({
  providedIn: "root",
})
export class NotificationsService {
  public showNotificationsSideBar = false;
  /** Located on Home Screen */
  public displayInviteModal: boolean = false;
  /** Blog post notifications */
  #notifications: WebsitePost[] = [];
  /** Invite object returned from the server */
  #invite: IUserInvite | undefined;
  /** @ignore */
  constructor(
    private client: HttpClient,
    private localStore: StorageService,
    private us: UserService,
    private api: ApiService,
    private cx: CatchErrorService
  ) {}

  get invite() {
    return this.#invite;
  }

  get hasInvite() {
    return (
      this.#invite !== undefined &&
      this.#invite.status === IUserInviteStatus.pending
    );
  }

  get notifications() {
    return this.#notifications;
  }

  /**
   * Number of unread blog posts + 1 if there is an active invite
   */
  get notificationCount(): number | undefined {
    const unreadCount = this.#notifications.filter((n) => n.show).length;
    return this.hasInvite
      ? unreadCount + 1
      : unreadCount === 0
      ? undefined
      : unreadCount;
  }

  /**
   * Init the notifications service, get:
   * - Blog posts
   * - Invite check
   */
  public async init(): Promise<void> {
    await Promise.all([this.getNotifications(), this.getInvites()]);
  }

  /**
   * Display the notifications side panel.
   *
   * - Component placed in `app.component.html`
   */
  toggleSidePanel(forceShow?: true): void {
    this.showNotificationsSideBar = forceShow ?? !this.showNotificationsSideBar;
  }

  private async getNotifications(): Promise<void> {
    try {
      const today = new Date();
      const lastYear = new Date(
        today.getFullYear(),
        today.getMonth() - 6,
        today.getDate()
      );
      const modifiedDate = `${lastYear.getFullYear()}-${(
        lastYear.getMonth() + 1
      )
        .toString()
        .padStart(2, "0")}-${lastYear
        .getDate()
        .toString()
        .padStart(2, "0")}T00:00:00Z`;

      let param: { [key: string]: any } = {};
      param["per_page"] = "10";
      param["modified_after"] = modifiedDate;
      param["keywords"] = "Updates";

      const httpParams = new HttpParams().appendAll(param);

      const req = this.client.get(
        "https://www.defectwise.com.au/wp-json/wp/v2/posts",
        {
          params: httpParams,
        }
      );

      const results = await Promise.all([
        firstValueFrom(req),
        this.getReadPostIds(),
      ]);

      const ret: WordpressPost[] = (results[0] as any)?.data ?? [];
      const postIds = results[1] as number[];
      const posts = ret.map((post) => {
        // trim excerpt
        let excerpt = post.excerpt.rendered
          .replace(/(<([^>]+)>)/gi, "")
          .replace(/&nbsp;/gi, " ")
          .replace(/\n/g, " ");

        if (excerpt.length > 200) {
          excerpt = excerpt.substring(0, 97);
        }

        return {
          id: post.id,
          title: post.title.rendered,
          excerpt: excerpt.trim() + "...",
          date: post.date,
          link: post.link,
          show: !postIds.includes(post.id),
        };
      }) as WebsitePost[];

      this.#notifications = posts;
    } catch (ex) {
      console.error(ex);
      return;
    }
  }
  /**
   * Check to see if user has a pending invite
   */
  private async getInvites(): Promise<void> {
    try {
      if (this.hasInvite === true) return;

      const email = await this.us.getEmail();
      if (email === null) return;

      const pendingInvite = await this.api.invite.getInvite(email);

      if (!environment.production) {
        console.log(pendingInvite);
      }

      if (pendingInvite.success && pendingInvite.data) {
        this.#invite = pendingInvite.data;
      }
    } catch (ex) {
      if (ex && JSON.stringify(ex).indexOf("#200") < 0) {
        await this.cx.handle(ex);
      }
      return Promise.resolve();
    }
  }

  /**
   * Get locally saved read post ids
   * @returns
   */
  private async getReadPostIds(): Promise<number[]> {
    const ids =
      (await this.localStore.getString(StorageKey.readMessageIds)) ?? "[]";
    const parsed = JSON.parse(ids);

    // check is array
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed;
  }

  /**
   * Hide a post and save the ids locally
   * @param id
   * @returns
   */
  async hidePost(id: number): Promise<void> {
    const ids = await this.getReadPostIds();
    // check if id is in ids
    if (ids.includes(id)) {
      return;
    }
    ids.push(id);
    await this.localStore.set(StorageKey.readMessageIds, JSON.stringify(ids));
  }

  /**
   * Remove the invite *after* accept or ignore.
   *
   * Sets the invite object to `undefined`
   */
  hideInvite(): void {
    this.#invite = undefined;
  }
}

export type WebsitePost = {
  readonly id: number;
  readonly title: string;
  readonly excerpt: string;
  readonly date: string;
  readonly link: string;
  show: boolean;
};

type WordpressPost = {
  readonly author: number;
  readonly categories: number[];
  readonly content: { rendered: string };
  readonly date: string;
  readonly date_gmt: string;
  readonly excerpt: { rendered: string };
  readonly featured_media: number;
  readonly format: string;
  readonly guid: { rendered: string };
  readonly id: number;
  readonly link: string;
  readonly modified: string;
  readonly modified_gmt: string;
  readonly slug: string;
  readonly status: "publish";
  readonly tags: number[];
  readonly title: { rendered: string };
  readonly type: "post";
};
